// 詳細ペイン「図面情報」「図面タグ」タブ。CAD から取り出したビューごとの文字とタグを表示する。取得は 1 回を両タブで共有する
import { ref, watch } from 'vue';

export const useCadInfo = ({ selectedItem, detailSubTab }) => {
  const cadInfoUrlTemplate = ref('');
  const cadInfoViews = ref([]);
  const cadInfoTags = ref({ part: [], material: [], layer: [] });
  const cadInfoLoading = ref(false);
  // レイヤーグループの開閉。キーは `${ビュー番号}:${グループの添字}`。キーが無いグループは既定 (開く) に従う
  const cadGroupOpenState = ref({});

  // 取得済み・取得中のファイル ID と、その取得の Promise。
  // タブを開く watch と一致情報からのジャンプが同時に呼んでも、取得は 1 回にする
  let loadedVideoId = null;
  let inflight = null;
  let requestSeq = 0;

  const resetCadInfo = () => {
    requestSeq += 1;
    cadInfoViews.value = [];
    cadInfoTags.value = { part: [], material: [], layer: [] };
    cadInfoLoading.value = false;
    cadGroupOpenState.value = {};
    loadedVideoId = null;
    inflight = null;
  };

  const applyResponse = (data) => {
    const views = Array.isArray(data?.views) ? data.views : [];
    // 古いデータ・rvt では layers が無いので [] にそろえる
    cadInfoViews.value = views.map((view) => ({
      ...view,
      layers: Array.isArray(view?.layers) ? view.layers : [],
    }));
    const tags = data?.tags || {};
    cadInfoTags.value = {
      part: Array.isArray(tags.part) ? tags.part : [],
      material: Array.isArray(tags.material) ? tags.material : [],
      layer: Array.isArray(tags.layer) ? tags.layer : [],
    };
  };

  const cadGroupKey = (viewNo, gIdx) => `${viewNo}:${gIdx}`;

  // 既定は全部開く
  const isCadGroupOpen = (viewNo, gIdx) => {
    const state = cadGroupOpenState.value[cadGroupKey(viewNo, gIdx)];
    return state === undefined ? true : state;
  };

  const toggleCadGroup = (viewNo, gIdx) => {
    cadGroupOpenState.value = {
      ...cadGroupOpenState.value,
      [cadGroupKey(viewNo, gIdx)]: !isCadGroupOpen(viewNo, gIdx),
    };
  };

  // 図面情報を取得する。取得済みなら通信せず終わる。
  // 戻り値の Promise は、反映が終わった (または捨てられた) 時点で解決する
  const ensureCadInfoLoaded = () => {
    const item = selectedItem.value;
    const template = cadInfoUrlTemplate.value;
    if (!item || (!item.has_cad_info && !item.has_cad_tags) || !template || !template.includes('__VIDEO_ID__')) {
      return Promise.resolve();
    }
    const videoId = String(item.id);
    if (loadedVideoId === videoId) {
      return inflight || Promise.resolve();
    }

    loadedVideoId = videoId;
    const seq = ++requestSeq;
    cadInfoLoading.value = true;
    inflight = (async () => {
      try {
        const url = template.replace('__VIDEO_ID__', encodeURIComponent(videoId));
        const response = await window.axios.get(url);
        // 待っている間に別のファイルへ切り替わっていたら捨てる
        if (seq !== requestSeq || String(selectedItem.value?.id) !== videoId) {
          return;
        }
        applyResponse(response?.data ?? null);
      } catch (error) {
        if (seq === requestSeq) {
          applyResponse(null);
          // 失敗したときは、次にタブを開いたときに取り直せるようにする
          loadedVideoId = null;
        }
      } finally {
        if (seq === requestSeq) {
          cadInfoLoading.value = false;
        }
      }
    })();
    return inflight;
  };

  const initCadInfo = (dataSource) => {
    cadInfoUrlTemplate.value =
      dataSource?.getAttribute('data-video-cad-info-url-template') || '';
  };

  // 開いているタブが図面情報 / 図面タグのどちらか。それぞれのタブが使えるかは has_cad_info / has_cad_tags で決まる
  const hasCadTab = (tab, item) => (tab === 'cad_info' ? !!item?.has_cad_info : !!item?.has_cad_tags);

  watch(selectedItem, (next) => {
    resetCadInfo();
    const tab = detailSubTab.value;
    if (tab !== 'cad_info' && tab !== 'cad_tags') {
      return;
    }
    // 開いているタブを持たないファイルに切り替わったのにタブだけ残ると、ボタンが消えて中身も空になる
    if (!hasCadTab(tab, next)) {
      detailSubTab.value = 'info';
      return;
    }
    // タブを開いたまま別の対象ファイルへ切り替わった場合は、取り直す
    void ensureCadInfoLoaded();
  });

  // タブを開いたときに取りに行く
  watch(detailSubTab, (next) => {
    if (next === 'cad_info' || next === 'cad_tags') {
      void ensureCadInfoLoaded();
    }
  });

  return {
    cadInfoViews,
    cadInfoTags,
    cadInfoLoading,
    isCadGroupOpen,
    toggleCadGroup,
    initCadInfo,
    ensureCadInfoLoaded,
  };
};
